import * as React from "react";
import type { PaymentRequest } from "../../../@apis/dto/request/payment.request";
import { useMutation } from "@tanstack/react-query";
import { payRequest } from "../../../@apis/v1/payment.service";
import Button from ".";
import { useDeviceStore } from "../../../lib/store/useDeviceStore";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";
import type { ErrorResponse } from "../../../@apis/dto/response/common.response";
import { useModalStore } from "../../../lib/store/useModalStore";

interface PaymentButtonProps {
  disabled?: boolean;
  type?: "tournament" | "resve";
  resveNum?: number;
  resveSeq?: number;
  applyId?: number;
  label?: string;
  onSuccess?: () => void;
  onFailed?: (message?: ErrorResponse) => void;
}

export default function PaymentButton({
  disabled = false,
  type = "resve",
  resveNum = 1,
  resveSeq = 0,
  applyId = 0,
  label = "결제하기",
  onFailed,
}: PaymentButtonProps) {
  const { deviceType } = useDeviceStore();
  const { myInfo } = useMyInfoStore();
  const { openModal } = useModalStore();

  const [paymentParams, setPaymentParams] = React.useState<PaymentRequest>();
  const formRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    window.__nicepaySubmitHandler = () => {
      openModal("ALERT_DIALOG", {
        title: "결제 완료",
        content: "결제가 완료되었습니다.",
        onConfirm: () => {
          if (formRef.current) {
            formRef.current.submit();
          }
        },
      });
    };

    window.__nicepayCloseHandler = () => {
      openModal("ALERT_DIALOG", {
        title: "결제 취소",
        content: "결제가 취소되었습니다.",
      });
    };

    return () => {
      window.__nicepaySubmitHandler = undefined;
      window.__nicepayCloseHandler = undefined;
    };
  }, []);

  const buildReqReserved = () => {
    if (type === "tournament") {
      return `tournament|${myInfo.userId}|${applyId}|1`;
    }
    return `${myInfo.userId}|${resveSeq}|${resveNum}`;
  };

  const getPayId = () => {
    return type === "tournament" ? applyId : resveSeq;
  };

  const payMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) =>
      payRequest({ id, type, resveNum }),
    onSuccess: (res) => {
      const { success, data, error } = res;
      if (success) {
        const backendParams = data.nicePayRequest;

        const finalParams = {
          ...backendParams,
          ReturnURL:
            import.meta.env.VITE_API_BASE_URL +
            "/v1/pay/nice-request/send-auth-response-and-pay-approve/by-form",
          ReqReserved: buildReqReserved(),
          WapUrl: "voicepark-pay://",
          IspCancelUrl: "voicepark-pay://",
        };

        setPaymentParams(finalParams);
      } else {
        console.error("결제 정보 요청 실패:", error);
        onFailed?.(error as ErrorResponse);
        alert("결제 정보를 가져오는 데 실패했습니다.");
      }
    },
    onError: (err) => {
      console.error("결제 정보 요청 에러:", err);
      alert("결제 중 오류가 발생했습니다.");
    },
  });

  React.useEffect(() => {
    if (paymentParams && formRef.current) {
      if (deviceType === "web") {
        window.goPay(formRef.current);
      } else {
        formRef.current.action = "https://web.nicepay.co.kr/v3/v3Payment.jsp";
        formRef.current.acceptCharset = "euc-kr";
        formRef.current.submit();
      }
    }
  }, [deviceType, paymentParams]);

  const handlePaymentClick = () => {
    payMutation.mutate({ id: getPayId() });
  };

  return (
    <div className="w-full">
      <Button
        onClick={handlePaymentClick}
        disabled={disabled}
        variant={disabled ? "gray" : "black"}
        className="w-full font-bold h-[50px]"
        label={label}
      />

      {paymentParams && (
        <form
          name="payForm"
          ref={formRef}
          action={paymentParams.ReturnURL}
          method="post"
          acceptCharset="EUC-KR"
        >
          {Object.entries(paymentParams).map(([key, value]) => (
            <input type="hidden" key={key} name={key} value={value} />
          ))}
        </form>
      )}
    </div>
  );
}
